/* VARAMO redesign concept — shared behaviour
   - smooth scroll to in-page anchors (menu "Watches" -> #clipeus)
   - PDP gallery carousel (arrows / swipe / arrow keys / thumb sync / counter)
   - PDP real variant resolution (swatches + sizes -> hidden id, price, add button)
   Safe to run on every page; each block no-ops when its markup is absent. */
(function () {
  'use strict';

  /* ---------- smooth anchor scroll ---------- */
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollToId(id, smooth) {
    var el = id && document.getElementById(id);
    if (!el) return false;
    el.scrollIntoView({ behavior: (smooth && !prefersReduced) ? 'smooth' : 'auto', block: 'start' });
    return true;
  }

  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var m = href.match(/#([A-Za-z0-9_-]+)$/);
      if (!m) return;
      // if the target exists on this page, scroll to it; otherwise let it navigate
      if (scrollToId(m[1], true)) {
        e.preventDefault();
        if (history.replaceState) history.replaceState(null, '', '#' + m[1]);
        // e.detail === 0 means keyboard/programmatic activation (Enter key) -
        // keep focus for accessibility there, but drop it after a real mouse
        // click so the overlay-nav's :focus-within reveal doesn't stick white.
        if (e.detail !== 0) a.blur();
      }
    });
    // arrived with a hash (e.g. from another page): ease into it
    if (location.hash.length > 1) {
      var id = location.hash.slice(1);
      window.addEventListener('load', function () {
        setTimeout(function () { scrollToId(id, true); }, 80);
      });
    }
  }

  /* ---------- gallery ---------- */
  function initGallery(root) {
    var main = root.querySelector('#mainImg, [data-gallery-main-img]');
    var thumbs = root.querySelector('[data-gallery-thumbs]');
    var gMain = root.querySelector('[data-gallery-stage]');
    if (!main || !thumbs || !gMain) return;
    var gCur = root.querySelector('[data-gallery-current]');
    var gTot = root.querySelector('[data-gallery-total]');
    var tBtns = Array.prototype.slice.call(thumbs.querySelectorAll('button'));
    var srcs = tBtns.map(function (b) { return b.getAttribute('data-src'); });
    if (!srcs.length) return;
    var idx = 0;
    if (gTot) gTot.textContent = String(srcs.length);

    // warm the browser cache so switching images never has to wait on the network
    srcs.forEach(function (url) { var p = new Image(); p.src = url; });

    function show(i, animate) {
      idx = (i + srcs.length) % srcs.length;
      var url = srcs[idx];
      var apply = function () {
        if (main.src === url) return;
        main.src = url;
        if (animate) { main.classList.remove('swap'); void main.offsetWidth; main.classList.add('swap'); }
      };
      // keep the current image on screen until the next one is fully decoded,
      // so switching never shows a blank/black frame while it loads
      var pre = new Image();
      pre.src = url;
      if (pre.decode) { pre.decode().then(apply, apply); }
      else if (pre.complete) { apply(); }
      else { pre.onload = apply; pre.onerror = apply; }

      if (gCur) gCur.textContent = String(idx + 1);
      tBtns.forEach(function (b, n) {
        if (n === idx) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      var act = tBtns[idx];
      if (act && act.scrollIntoView) act.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    thumbs.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      show(tBtns.indexOf(b), true);
    });
    // a strap/colour pick jumps to that variant's own image
    root.addEventListener('concept:variant-media', function (e) {
      var n = tBtns.findIndex(function (b) { return b.getAttribute('data-media-id') === String(e.detail); });
      if (n > -1 && n !== idx) show(n, true);
    });
    gMain.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1, true); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1, true); }
    });
    var x0 = null;
    gMain.addEventListener('pointerdown', function (e) { x0 = e.clientX; });
    gMain.addEventListener('pointerup', function (e) {
      if (x0 === null) return;
      var dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 45) show(idx + (dx < 0 ? 1 : -1), true);
    });
    gMain.addEventListener('pointercancel', function () { x0 = null; });
  }

  /* ---------- variant resolution ---------- */
  function initVariants(root) {
    var dataEl = root.querySelector('[data-variant-json]');
    var form = root.querySelector('form.concept-buy-form') || root.querySelector('form[action*="/cart/add"]');
    if (!dataEl || !form) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var idInput = form.querySelector('input[name="id"]');
    var addBtn = form.querySelector('[data-add-button]');
    var priceEl = root.querySelector('[data-price]');
    var addLabel = root.querySelector('[data-add-label]');
    var stockEl = root.querySelector('[data-stock-note]');
    var LOW_STOCK_THRESHOLD = 8;

    // option group elements: each has data-option-position
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-option-position]'));
    var optCount = groups.length;

    function selected() {
      var vals = [];
      groups.forEach(function (g) {
        var pos = parseInt(g.getAttribute('data-option-position'), 10);
        var btn = g.querySelector('button[aria-pressed="true"]:not([disabled])') || g.querySelector('button[aria-pressed="true"]');
        vals[pos - 1] = btn ? btn.getAttribute('data-value') : null;
      });
      return vals;
    }

    function matchVariant(vals) {
      return variants.find(function (v) {
        for (var i = 0; i < optCount; i++) {
          if (vals[i] == null) return false;
          if (v.options[i] !== vals[i]) return false;
        }
        return true;
      }) || null;
    }

    function refreshAvailability(vals) {
      // For each group, disable a value if no available variant exists given the
      // other currently-selected option values.
      groups.forEach(function (g) {
        var pos = parseInt(g.getAttribute('data-option-position'), 10) - 1;
        var isSizeGroup = !!g.querySelector('.sizes');
        Array.prototype.forEach.call(g.querySelectorAll('button'), function (btn) {
          var probe = vals.slice();
          probe[pos] = btn.getAttribute('data-value');
          var ok = variants.some(function (v) {
            for (var i = 0; i < optCount; i++) {
              if (i === pos) { if (v.options[i] !== probe[i]) return false; }
              else if (probe[i] != null && v.options[i] !== probe[i]) return false;
            }
            return v.available;
          });

          if (isSizeGroup) {
            // Only show the size values that actually pair with the
            // currently-selected value of the other option (e.g. strap),
            // regardless of stock — never fall back to a different
            // option's sizes just because the paired one is sold out.
            var existsPaired = variants.some(function (v) {
              for (var i = 0; i < optCount; i++) {
                if (i === pos) { if (v.options[i] !== probe[i]) return false; }
                else if (probe[i] != null && v.options[i] !== probe[i]) return false;
              }
              return true;
            });
            btn.disabled = existsPaired ? !ok : true;
            btn.hidden = !existsPaired;
          } else {
            // Strap/colour swatches must always stay clickable so the
            // shopper can switch strap regardless of the other option's
            // stock state.
            var existsAtAll = variants.some(function (v) { return v.options[pos] === btn.getAttribute('data-value'); });
            btn.disabled = false;
            btn.hidden = !existsAtAll;
          }
        });
        var active = g.querySelector('button[aria-pressed="true"]');
        if (isSizeGroup) {
          // Sizes are never guessed: a lone size (e.g. steel bracelet = one
          // size) is picked automatically, otherwise the shopper chooses.
          if (!active || active.disabled || active.hidden) {
            var open = g.querySelectorAll('button:not([hidden])');
            Array.prototype.forEach.call(g.querySelectorAll('button'), function (b) { b.setAttribute('aria-pressed', 'false'); });
            var sizeLbl = g.querySelector('[data-option-value-label]');
            if (open.length === 1) {
              open[0].setAttribute('aria-pressed', 'true');
              if (sizeLbl) { sizeLbl.textContent = open[0].getAttribute('data-value'); sizeLbl.classList.remove('is-unset'); }
            } else if (sizeLbl) {
              sizeLbl.textContent = 'Select';
              sizeLbl.classList.add('is-unset');
            }
          }
          return;
        }
        // if the active button became disabled, move selection to first enabled
        if (active && (active.disabled || active.hidden)) {
          var alt = g.querySelector('button:not([disabled]):not([hidden])');
          if (alt) {
            Array.prototype.forEach.call(g.querySelectorAll('button'), function (b) { b.setAttribute('aria-pressed', 'false'); });
            alt.setAttribute('aria-pressed', 'true');
            var lbl = g.querySelector('[data-option-value-label]');
            if (lbl) lbl.textContent = alt.getAttribute('data-value');
          }
        }
      });
    }

    function sizeGroups() {
      return groups.filter(function (g) { return !!g.querySelector('.sizes'); });
    }
    function isSizePos(i) {
      return sizeGroups().some(function (g) { return parseInt(g.getAttribute('data-option-position'), 10) - 1 === i; });
    }
    function missingSize() {
      return sizeGroups().filter(function (g) { return !g.querySelector('button[aria-pressed="true"]'); })[0] || null;
    }

    form.addEventListener('submit', function (e) {
      var g = missingSize();
      if (!g) return;
      e.preventDefault();
      var prompt = g.querySelector('[data-size-prompt]');
      if (prompt) prompt.hidden = false;
      g.classList.remove('needs-size');
      void g.offsetWidth; // restart the nudge if they click again
      g.classList.add('needs-size');
      var r = g.getBoundingClientRect();
      if (r.top < 80 || r.bottom > window.innerHeight) {
        g.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    function update() {
      var vals = selected();
      refreshAvailability(vals);
      vals = selected();
      var v = matchVariant(vals);
      if (v) {
        if (idInput) idInput.value = v.id;
        if (priceEl && v.price_html) priceEl.innerHTML = v.price_html;
        if (addBtn) {
          addBtn.disabled = !v.available;
          if (addLabel) addLabel.textContent = v.available ? (addBtn.getAttribute('data-label-add') || 'Add to bag') : (addBtn.getAttribute('data-label-sold') || 'Sold out');
        }
        if (stockEl) {
          var qty = v.inventory_quantity;
          var low = v.available && v.inventory_management === 'shopify' && qty > 0 && qty <= LOW_STOCK_THRESHOLD;
          if (low) {
            stockEl.textContent = 'Only ' + qty + ' left';
            stockEl.hidden = false;
          } else {
            stockEl.hidden = true;
          }
        }
      } else if (addBtn && missingSize()) {
        // No size chosen yet: keep the button inviting, show the price of the
        // chosen strap, and ask for the size only once they try to add.
        var known = vals.map(function (x, i) { return isSizePos(i) ? null : x; });
        var any = variants.filter(function (c) {
          return known.every(function (x, i) { return x == null || c.options[i] === x; });
        });
        var pick = any.find(function (c) { return c.available; }) || any[0];
        if (priceEl && pick && pick.price_html) priceEl.innerHTML = pick.price_html;
        var canBuy = any.some(function (c) { return c.available; });
        addBtn.disabled = !canBuy;
        if (addLabel) addLabel.textContent = canBuy ? (addBtn.getAttribute('data-label-add') || 'Add to bag') : (addBtn.getAttribute('data-label-sold') || 'Sold out');
        if (stockEl) stockEl.hidden = true;
      } else if (addBtn) {
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = addBtn.getAttribute('data-label-unavail') || 'Unavailable';
        if (stockEl) stockEl.hidden = true;
      }
    }

    groups.forEach(function (g) {
      g.addEventListener('click', function (e) {
        if (e.target.closest('.size-guide')) return;
        var b = e.target.closest('button'); if (!b || b.disabled) return;
        Array.prototype.forEach.call(g.querySelectorAll('button'), function (x) { x.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        var lbl = g.querySelector('[data-option-value-label]');
        if (lbl) { lbl.textContent = b.getAttribute('data-value'); lbl.classList.remove('is-unset'); }
        var prompt = g.querySelector('[data-size-prompt]');
        if (prompt) prompt.hidden = true;
        g.classList.remove('needs-size');
        var note = g.querySelector('[data-option-note]');
        if (note) {
          var n = b.getAttribute('data-note');
          if (n) { note.innerHTML = n + '<sup>*</sup>'; note.hidden = false; }
          else { note.hidden = true; }
        }
        update();
        if (!g.querySelector('.sizes')) {
          var pos = parseInt(g.getAttribute('data-option-position'), 10) - 1;
          var hit = variants.find(function (v) { return v.options[pos] === b.getAttribute('data-value') && v.media_id; });
          var gallery = root.querySelector('[data-concept-gallery]');
          if (hit && gallery) gallery.dispatchEvent(new CustomEvent('concept:variant-media', { detail: hit.media_id }));
        }
      });
    });

    update();
  }

  /* ---------- overlay nav height (so the hero can sit exactly underneath it) ---------- */
  function measureOverlayNav() {
    var nav = document.querySelector('.concept-nav.nav-overlay');
    if (!nav) return;
    document.documentElement.style.setProperty('--nav-h', nav.offsetHeight + 'px');
  }

  /* ---------- overlay nav: stays transparent on scroll, only the scrim
     fades in past the very top; solid white is hover/focus-only (CSS) ---------- */
  var overlayScrollBound = false;
  function initOverlayNavScroll() {
    var nav = document.querySelector('.concept-nav.nav-overlay');
    if (!nav || overlayScrollBound) return;
    overlayScrollBound = true;
    var ticking = false;
    function apply() {
      nav.classList.toggle('nav-at-top', window.scrollY <= 0);
      ticking = false;
    }
    apply();
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }, { passive: true });
  }

  /* ---------- newsletter tab: visible from the Clipeus section onward, hides
     again once you scroll back up past it towards the header ---------- */
  var newsletterRevealBound = false;
  function initNewsletterReveal() {
    var tab = document.querySelector('.concept-newsletter-tab');
    if (!tab || newsletterRevealBound) return;
    var trigger = document.getElementById('clipeus');
    if (!trigger) { tab.classList.add('is-visible'); return; }
    newsletterRevealBound = true;

    // #clipeus carries scroll-margin-top so anchor jumps land ~9rem below the
    // viewport top (clear of the sticky nav) - match that same offset here so
    // the tab reveals right as the section arrives, not only once scrolled
    // further past it. On phones the tab docks at the bottom, right where the
    // stacked Clipeus pieces sit, so wait until that whole section has
    // scrolled by instead of revealing at its very start.
    var REVEAL_OFFSET = 180;
    var mobileQuery = window.matchMedia('(max-width: 600px)');
    var ticking = false;
    function apply() {
      var rect = trigger.getBoundingClientRect();
      var reached = mobileQuery.matches ? (rect.bottom <= 0) : (rect.top <= REVEAL_OFFSET);
      tab.classList.toggle('is-visible', reached);
      ticking = false;
    }
    apply();
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }, { passive: true });
    window.addEventListener('resize', apply);
  }

  /* ---------- newsletter tab: centered-modal open/close behaviour ---------- */
  var newsletterModalBound = false;
  function initNewsletterModal() {
    var wrap = document.querySelector('.concept-newsletter-tab');
    if (!wrap || newsletterModalBound) return;
    var details = wrap.querySelector('.tab');
    var summary = wrap.querySelector('summary');
    if (!details || !summary) return;
    newsletterModalBound = true;

    details.addEventListener('toggle', function () {
      document.documentElement.classList.toggle('newsletter-modal-open', details.open);
    });
    document.addEventListener('click', function (e) {
      if (!details.open) return;
      if (e.target.closest('.tab-backdrop') || e.target.closest('.tab-close') || !details.contains(e.target)) {
        details.open = false;
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && details.open) {
        details.open = false;
        summary.focus();
      }
    });
  }

  var sizeGuideBound = false;
  function initSizeGuide() {
    var items = document.querySelectorAll('.size-guide');
    if (!items.length || sizeGuideBound) return;
    sizeGuideBound = true;

    items.forEach(function (details) {
      var summary = details.querySelector('summary');
      details.addEventListener('toggle', function () {
        document.documentElement.classList.toggle('size-guide-open', details.open);
      });
      details.addEventListener('click', function (e) {
        if (!details.open) return;
        if (e.target.closest('.size-guide-backdrop') || e.target.closest('.size-guide-close')) {
          e.stopPropagation();
          details.open = false;
          if (summary) summary.focus();
        }
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      items.forEach(function (details) {
        if (!details.open) return;
        details.open = false;
        var summary = details.querySelector('summary');
        if (summary) summary.focus();
      });
    });

    if (window.location.hash === '#size-guide') {
      var target = document.getElementById('size-guide');
      if (target) {
        target.open = true;
        target.scrollIntoView({ block: 'center' });
      }
    }
  }

  /* ---------- keep the hero quote no wider than the brand name above it ---------- */
  function fitHeroQuote() {
    var brand = document.querySelector('.concept-hero .hero-brand');
    var quote = document.querySelector('.concept-hero .hero-quote');
    if (!brand || !quote) return;
    quote.style.maxWidth = brand.offsetWidth + 'px';
  }

  /* ---------- watch real element size instead of the 'resize' event ----------
     Phones fire 'resize' mid-rotation (before the browser chrome/toolbar has
     settled), so measuring on that event can lock in a stale width/height that
     never corrects itself once the rotation finishes - the page then looks
     stretched/squashed until a manual reload. ResizeObserver reports the
     settled size instead, so it self-heals through a rotation in either
     direction. */
  var geometryObserver = null;
  function observeGeometry() {
    if (geometryObserver || typeof ResizeObserver === 'undefined') return;
    geometryObserver = new ResizeObserver(function () {
      measureOverlayNav();
      fitHeroQuote();
    });
    var nav = document.querySelector('.concept-nav.nav-overlay');
    var brand = document.querySelector('.concept-hero .hero-brand');
    if (nav) geometryObserver.observe(nav);
    if (brand) geometryObserver.observe(brand);
    geometryObserver.observe(document.documentElement);
  }

  var anchorsReady = false;
  function boot() {
    if (!anchorsReady) { initAnchors(); anchorsReady = true; }
    document.querySelectorAll('[data-concept-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-concept-pdp]').forEach(initVariants);
    measureOverlayNav();
    fitHeroQuote();
    observeGeometry();
    initOverlayNavScroll();
    initNewsletterReveal();
    initNewsletterModal();
    initSizeGuide();
  }

  window.addEventListener('resize', function () {
    measureOverlayNav();
    fitHeroQuote();
  });
  window.addEventListener('orientationchange', function () {
    // let the browser settle the viewport/toolbar before re-measuring
    setTimeout(function () { measureOverlayNav(); fitHeroQuote(); }, 200);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { measureOverlayNav(); fitHeroQuote(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify theme editor re-renders sections
  document.addEventListener('shopify:section:load', boot);
})();
